import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { publicUrl } from '../../utils/publicUrl';

export type FixedViewerLayoutId = 'singleAxial' | 'singleCoronal' | 'singleSagittal' | 'only3D';
export type ProjectViewerGridLayout = 1 | 2 | 4 | 6;

export const PROJECT_VIEWER_SLICE_THUMBNAIL_MESSAGE = 'neuroplan:project-viewer-slice-thumbnail';
export const PROJECT_VIEWER_ACTIVE_VIEW_MESSAGE = 'neuroplan:project-viewer-active-view';
export const PROJECT_VIEWER_ROTATION_MESSAGE = 'neuroplan:project-viewer-rotation';
export type ProjectViewerOrientation = 'axial' | 'coronal' | 'sagittal';
export type ProjectViewerSelectedView = ProjectViewerOrientation | '3d';
export type ProjectViewerResetTarget = ProjectViewerOrientation | '3d';

export interface ProjectViewerSliceThumbnail {
  type: typeof PROJECT_VIEWER_SLICE_THUMBNAIL_MESSAGE;
  studyInstanceUid: string;
  seriesInstanceUid: string;
  orientation: ProjectViewerOrientation;
  imageIndex: number | null;
  thumbnailSrc: string;
}

export interface ProjectViewerPanResetRequest {
  orientation: ProjectViewerResetTarget;
  requestId: number;
}

export interface ProjectViewerParcelizationVolume {
  id: string;
  label: string;
  color: [number, number, number];
  hemisphere: 'left' | 'right';
  niftiUrl: string;
}

export interface ProjectViewerRotation {
  type: typeof PROJECT_VIEWER_ROTATION_MESSAGE;
  viewportId: string;
  axis: [number, number, number];
  angleDegrees: number;
  translation?: [number, number, number];
}

export type OHIFHangingProtocolId =
  | FixedViewerLayoutId
  | 'projectSeriesPreview'
  | 'projectViewerPersistent'
  | 'projectViewer2With3D'
  | 'projectViewer4With3D'
  | 'projectViewer6With3D';

interface OHIFEmbeddedViewerProps {
  uploadId: string;
  studyInstanceUid: string;
  seriesInstanceUid: string;
  niftiAssetId?: string;
  modality?: string;
  viewerConfigPath?: string;
  hangingProtocolId?: FixedViewerLayoutId;
  gridLayout?: ProjectViewerGridLayout;
  orientationRequestVersion?: number;
  className?: string;
  loading?: 'eager' | 'lazy';
  onSliceThumbnail?: (thumbnail: ProjectViewerSliceThumbnail) => void;
  panResetRequest?: ProjectViewerPanResetRequest | null;
  onViewerInteraction?: () => void;
  onRotation?: (rotation: ProjectViewerRotation) => void;
  reloadVersion?: number;
  isEditing?: boolean;
  parcelizations?: ProjectViewerParcelizationVolume[];
  parcelizationOnly?: boolean;
  onActiveView?: (view: ProjectViewerSelectedView) => void;
  registrationSyncRole?: 'master' | 'slave';
  registrationState?: ProjectViewerRegistrationViewportState | null;
  onRegistrationState?: (state: ProjectViewerRegistrationViewportState) => void;
  registrationAlignmentEnabled?: boolean;
  registrationTransforms?: ProjectViewerRegistrationTransform[];
  onRegistrationTransform?: (transform: ProjectViewerRegistrationTransform) => void;
  registrationSyncVoi?: boolean;
  registrationSyncCamera?: boolean;
}

type ViewerUrlOptions = {
  hangingProtocolId?: OHIFHangingProtocolId;
  previewMode?: boolean;
  projectViewerMode?: boolean;
  projectViewerOverlay?: boolean;
  projectViewerRegistration?: 'master' | 'slave';
  niftiAssetId?: string;
  modality?: string;
  viewerConfigPath?: string;
};

const PROJECT_VIEWER_STATE_MESSAGE = 'neuroplan:set-project-viewer-state';
export const PROJECT_VIEWER_READY_MESSAGE = 'neuroplan:project-viewer-ready';
const PROJECT_VIEWER_PARCELLIZATION_READY_MESSAGE = 'neuroplan:project-viewer-parcellization-ready';
export const PROJECT_VIEWER_RESET_PAN_MESSAGE = 'neuroplan:reset-project-viewer-pan';
export const PROJECT_VIEWER_EDIT_MODE_MESSAGE = 'neuroplan:set-project-viewer-edit-mode';
export const PROJECT_VIEWER_PARCELLIZATION_MESSAGE = 'neuroplan:set-project-viewer-parcellizations';
export const PROJECT_VIEWER_REGISTRATION_STATE_MESSAGE =
  'neuroplan:project-viewer-registration-state';
export const PROJECT_VIEWER_REGISTRATION_APPLY_MESSAGE =
  'neuroplan:apply-project-viewer-registration-state';
export const PROJECT_VIEWER_REGISTRATION_MODE_MESSAGE =
  'neuroplan:set-project-viewer-registration-mode';
export const PROJECT_VIEWER_REGISTRATION_MODE_READY_MESSAGE =
  'neuroplan:project-viewer-registration-mode-ready';
export const PROJECT_VIEWER_REGISTRATION_TRANSFORM_APPLY_MESSAGE =
  'neuroplan:apply-project-viewer-registration-transform';

export interface ProjectViewerRegistrationViewportState {
  camera?: {
    position: number[];
    focalPoint: number[];
    viewUp: number[];
    viewPlaneNormal?: number[];
    parallelScale?: number;
  };
  voiRange?: { lower: number; upper: number };
  voiRelative?: { windowWidthScale: number; windowCenterOffset: number };
  sliceFraction?: number;
  zoom?: number;
}

export interface ProjectViewerRegistrationTransform extends ProjectViewerRotation {
  requestId: number;
}

function isProjectViewerSliceThumbnail(value: unknown): value is ProjectViewerSliceThumbnail {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const thumbnail = value as Partial<ProjectViewerSliceThumbnail>;
  return (
    thumbnail.type === PROJECT_VIEWER_SLICE_THUMBNAIL_MESSAGE &&
    typeof thumbnail.studyInstanceUid === 'string' &&
    typeof thumbnail.seriesInstanceUid === 'string' &&
    ['axial', 'coronal', 'sagittal'].includes(thumbnail.orientation as string) &&
    (thumbnail.imageIndex === null || typeof thumbnail.imageIndex === 'number') &&
    typeof thumbnail.thumbnailSrc === 'string' &&
    thumbnail.thumbnailSrc.startsWith('data:image/')
  );
}

function isProjectViewerRotation(value: unknown): value is ProjectViewerRotation {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const rotation = value as Partial<ProjectViewerRotation>;
  return (
    rotation.type === PROJECT_VIEWER_ROTATION_MESSAGE &&
    typeof rotation.viewportId === 'string' &&
    Array.isArray(rotation.axis) &&
    rotation.axis.length === 3 &&
    rotation.axis.every(
      coordinate => typeof coordinate === 'number' && Number.isFinite(coordinate)
    ) &&
    typeof rotation.angleDegrees === 'number' &&
    Number.isFinite(rotation.angleDegrees) &&
    (rotation.translation === undefined ||
      (Array.isArray(rotation.translation) &&
        rotation.translation.length === 3 &&
        rotation.translation.every(
          coordinate => typeof coordinate === 'number' && Number.isFinite(coordinate)
        ))) &&
    (rotation.angleDegrees !== 0 || rotation.translation?.some(coordinate => coordinate !== 0))
  );
}
const TWO_DIMENSIONAL_LAYOUT_ORIENTATIONS = {
  singleAxial: 'axial',
  singleCoronal: 'coronal',
  singleSagittal: 'sagittal',
} as const;
export function getInitialProjectViewerProtocol(
  _hangingProtocolId: FixedViewerLayoutId,
  _gridLayout: ProjectViewerGridLayout
): OHIFHangingProtocolId {
  // One permanent MPR + 3D-capable document serves every 1/2/4/6 layout.
  // Cornerstone keeps the decoded volume and GPU cache alive while only the
  // lightweight viewport arrangement changes.
  return 'projectViewerPersistent';
}

export function buildOHIFViewerUrl(
  uploadId: string,
  studyInstanceUid: string,
  seriesInstanceUid: string,
  options: ViewerUrlOptions = {}
): string {
  const {
    hangingProtocolId = 'singleAxial',
    previewMode = false,
    projectViewerMode = false,
    projectViewerOverlay = false,
    projectViewerRegistration,
    niftiAssetId,
    modality,
    viewerConfigPath,
  } = options;
  const viewerUrl = new URL('viewer/dicomjson', new URL(publicUrl, window.location.origin));
  const configPath =
    viewerConfigPath ??
    (niftiAssetId
      ? `/dicom/uploads/${encodeURIComponent(uploadId)}/prepared-nifti/${encodeURIComponent(
          niftiAssetId
        )}/viewer-config`
      : `/dicom/uploads/${encodeURIComponent(uploadId)}/studies/${encodeURIComponent(
          studyInstanceUid
        )}/series/${encodeURIComponent(seriesInstanceUid)}/viewer-config`);
  const configUrl = new URL(`/api/v1${configPath}`, window.location.origin);

  viewerUrl.searchParams.set('url', configUrl.toString());
  viewerUrl.searchParams.set('StudyInstanceUIDs', studyInstanceUid);
  viewerUrl.searchParams.set('SeriesInstanceUIDs', seriesInstanceUid);
  viewerUrl.searchParams.set('initialSeriesInstanceUID', seriesInstanceUid);
  viewerUrl.searchParams.set('hangingprotocolid', hangingProtocolId);
  if (previewMode) {
    viewerUrl.searchParams.set('projectpreview', '1');
  }
  if (projectViewerMode) {
    viewerUrl.searchParams.set('projectviewer', '1');
  }
  if (projectViewerOverlay) {
    viewerUrl.searchParams.set('projectvieweroverlay', '1');
  }
  if (projectViewerRegistration) {
    viewerUrl.searchParams.set('projectviewerregistration', projectViewerRegistration);
  }
  if (projectViewerMode && modality?.trim().toUpperCase() === 'CT') {
    // Native CT series need a deterministic first camera/VOI initialization.
    // The embedded Cornerstone viewport uses this marker to recover when the
    // expanding project panel was still zero-sized during its first render.
    viewerUrl.searchParams.set('projectctviewer', '1');
  }
  if (viewerConfigPath?.startsWith('/atlases/')) {
    viewerUrl.searchParams.set('atlasviewer', '1');
  }

  return viewerUrl.toString();
}

const OHIFEmbeddedViewer: React.FC<OHIFEmbeddedViewerProps> = ({
  uploadId,
  studyInstanceUid,
  seriesInstanceUid,
  niftiAssetId,
  modality,
  viewerConfigPath,
  hangingProtocolId = 'singleAxial',
  gridLayout = 1,
  orientationRequestVersion = 0,
  className = 'pc-ohif-embedded-frame',
  loading = 'eager',
  onSliceThumbnail,
  panResetRequest,
  onViewerInteraction,
  onRotation,
  reloadVersion = 0,
  isEditing = false,
  parcelizations = [],
  parcelizationOnly = false,
  onActiveView,
  registrationSyncRole,
  registrationState = null,
  onRegistrationState,
  registrationAlignmentEnabled = false,
  registrationTransforms,
  onRegistrationTransform,
  registrationSyncVoi = true,
  registrationSyncCamera = true,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const messageSequenceRef = useRef(0);
  const sentRegistrationTransformIdsRef = useRef<Set<number>>(new Set());
  const viewerInteractionCleanupRef = useRef<(() => void) | null>(null);
  const initialHangingProtocolId = getInitialProjectViewerProtocol(hangingProtocolId, gridLayout);
  const layoutProvidedByHangingProtocol = false;
  const viewerSrc = useMemo(
    () =>
      buildOHIFViewerUrl(uploadId, studyInstanceUid, seriesInstanceUid, {
        hangingProtocolId: initialHangingProtocolId,
        previewMode: true,
        projectViewerMode: true,
        niftiAssetId,
        modality,
        viewerConfigPath,
        projectViewerOverlay: registrationSyncRole === 'slave',
        projectViewerRegistration: registrationSyncRole,
      }),
    [
      initialHangingProtocolId,
      niftiAssetId,
      modality,
      seriesInstanceUid,
      studyInstanceUid,
      uploadId,
      viewerConfigPath,
      registrationSyncRole,
    ]
  );
  const sendRequestedViewerState = useCallback(() => {
    const viewerWindow = iframeRef.current?.contentWindow;
    if (!viewerWindow) {
      return;
    }

    const selectedView =
      hangingProtocolId === 'only3D'
        ? '3d'
        : TWO_DIMENSIONAL_LAYOUT_ORIENTATIONS[hangingProtocolId];
    viewerWindow.postMessage(
      {
        type: PROJECT_VIEWER_STATE_MESSAGE,
        gridLayout,
        selectedView,
        layoutProvidedByHangingProtocol,
        orientationRequestVersion,
        requestId: ++messageSequenceRef.current,
      },
      window.location.origin
    );
  }, [gridLayout, hangingProtocolId, layoutProvidedByHangingProtocol, orientationRequestVersion]);

  const sendRequestedPanReset = useCallback(() => {
    const viewerWindow = iframeRef.current?.contentWindow;
    if (!viewerWindow || !panResetRequest) {
      return;
    }

    viewerWindow.postMessage(
      {
        type: PROJECT_VIEWER_RESET_PAN_MESSAGE,
        orientation: panResetRequest.orientation,
        requestId: panResetRequest.requestId,
      },
      window.location.origin
    );
  }, [panResetRequest]);

  const sendRequestedEditMode = useCallback(() => {
    const viewerWindow = iframeRef.current?.contentWindow;
    if (!viewerWindow) {
      return;
    }

    viewerWindow.postMessage(
      {
        type: PROJECT_VIEWER_EDIT_MODE_MESSAGE,
        isEditing,
      },
      window.location.origin
    );
  }, [isEditing]);

  const sendRequestedParcelizations = useCallback(() => {
    const viewerWindow = iframeRef.current?.contentWindow;
    if (!viewerWindow) {
      return;
    }
    viewerWindow.postMessage(
      {
        type: PROJECT_VIEWER_PARCELLIZATION_MESSAGE,
        volumes: parcelizations,
        showOnlyParcelizations: parcelizationOnly,
      },
      window.location.origin
    );
  }, [parcelizationOnly, parcelizations]);

  const sendRegistrationState = useCallback(() => {
    if (registrationSyncRole !== 'slave' || !registrationState) {
      return;
    }

    const stateToSend =
      registrationSyncVoi === false || registrationSyncCamera === false
        ? (() => {
            const {
              voiRange: _voiRange,
              voiRelative: _voiRelative,
              camera: _camera,
              ...stateWithoutUnsafeFields
            } = registrationState;
            return {
              ...stateWithoutUnsafeFields,
              ...(registrationSyncVoi === false
                ? {}
                : {
                    ...(registrationState.voiRange ? { voiRange: registrationState.voiRange } : {}),
                    ...(registrationState.voiRelative
                      ? { voiRelative: registrationState.voiRelative }
                      : {}),
                  }),
              ...(registrationSyncCamera === false ? {} : { camera: registrationState.camera }),
            };
          })()
        : registrationState;

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: PROJECT_VIEWER_REGISTRATION_APPLY_MESSAGE,
        state: stateToSend,
      },
      window.location.origin
    );
  }, [registrationState, registrationSyncCamera, registrationSyncRole, registrationSyncVoi]);

  const sendRegistrationMode = useCallback(() => {
    if (registrationSyncRole !== 'master') {
      return;
    }

    iframeRef.current?.contentWindow?.postMessage(
      {
        type: PROJECT_VIEWER_REGISTRATION_MODE_MESSAGE,
        enabled: registrationAlignmentEnabled,
      },
      window.location.origin
    );
  }, [registrationAlignmentEnabled, registrationSyncRole]);

  const sendRegistrationTransforms = useCallback(() => {
    if (registrationSyncRole !== 'slave' || !registrationTransforms?.length) {
      return;
    }

    registrationTransforms.forEach(transform => {
      if (sentRegistrationTransformIdsRef.current.has(transform.requestId)) {
        return;
      }
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: PROJECT_VIEWER_REGISTRATION_TRANSFORM_APPLY_MESSAGE,
          transform,
        },
        window.location.origin
      );
      sentRegistrationTransformIdsRef.current.add(transform.requestId);
    });
  }, [registrationSyncRole, registrationTransforms]);

  useEffect(() => {
    sendRequestedViewerState();
  }, [sendRequestedViewerState]);

  useEffect(() => {
    sendRequestedPanReset();
  }, [sendRequestedPanReset]);

  useEffect(() => {
    sendRequestedEditMode();
  }, [sendRequestedEditMode]);

  useEffect(() => {
    sendRequestedParcelizations();
  }, [sendRequestedParcelizations]);

  useEffect(() => {
    sendRegistrationState();
  }, [sendRegistrationState]);

  useEffect(() => {
    sendRegistrationMode();
  }, [sendRegistrationMode]);

  useEffect(() => {
    sendRegistrationTransforms();
  }, [sendRegistrationTransforms]);

  const attachViewerInteractionListener = useCallback(() => {
    viewerInteractionCleanupRef.current?.();
    viewerInteractionCleanupRef.current = null;

    if (!onViewerInteraction) {
      return;
    }

    try {
      const viewerDocument = iframeRef.current?.contentDocument;
      if (!viewerDocument) {
        return;
      }

      viewerDocument.addEventListener('pointerdown', onViewerInteraction, true);
      viewerInteractionCleanupRef.current = () =>
        viewerDocument.removeEventListener('pointerdown', onViewerInteraction, true);
    } catch {
      // The iframe focus handler below remains as a cross-origin fallback.
    }
  }, [onViewerInteraction]);

  useEffect(() => {
    attachViewerInteractionListener();
    return () => {
      viewerInteractionCleanupRef.current?.();
      viewerInteractionCleanupRef.current = null;
    };
  }, [attachViewerInteractionListener]);

  const handleViewerLoad = useCallback(() => {
    sentRegistrationTransformIdsRef.current.clear();
    attachViewerInteractionListener();
    sendRequestedViewerState();
    sendRequestedPanReset();
    sendRequestedEditMode();
    sendRequestedParcelizations();
    sendRegistrationState();
    sendRegistrationMode();
    sendRegistrationTransforms();
  }, [
    attachViewerInteractionListener,
    sendRequestedEditMode,
    sendRequestedPanReset,
    sendRequestedViewerState,
    sendRequestedParcelizations,
    sendRegistrationMode,
    sendRegistrationState,
    sendRegistrationTransforms,
  ]);

  useEffect(() => {
    const handleViewerMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== iframeRef.current?.contentWindow
      ) {
        return;
      }

      if (event.data?.type === PROJECT_VIEWER_READY_MESSAGE) {
        sendRequestedViewerState();
        sendRequestedPanReset();
        sendRequestedEditMode();
        sendRequestedParcelizations();
        sendRegistrationState();
        sendRegistrationMode();
        sendRegistrationTransforms();
        return;
      }

      if (
        registrationSyncRole === 'master' &&
        event.data?.type === PROJECT_VIEWER_REGISTRATION_MODE_READY_MESSAGE
      ) {
        sendRegistrationMode();
        return;
      }

      if (event.data?.type === PROJECT_VIEWER_PARCELLIZATION_READY_MESSAGE) {
        sendRequestedParcelizations();
        return;
      }

      if (
        registrationSyncRole === 'master' &&
        onRegistrationState &&
        event.data?.type === PROJECT_VIEWER_REGISTRATION_STATE_MESSAGE &&
        event.data?.state &&
        typeof event.data.state === 'object'
      ) {
        onRegistrationState(event.data.state as ProjectViewerRegistrationViewportState);
        return;
      }

      if (
        registrationSyncRole === 'master' &&
        registrationAlignmentEnabled &&
        onRegistrationTransform &&
        isProjectViewerRotation(event.data)
      ) {
        onRegistrationTransform({
          ...event.data,
          requestId: ++messageSequenceRef.current,
        });
        return;
      }

      if (onRotation && isProjectViewerRotation(event.data)) {
        onRotation(event.data);
        return;
      }

      if (
        onActiveView &&
        event.data?.type === PROJECT_VIEWER_ACTIVE_VIEW_MESSAGE &&
        ['axial', 'coronal', 'sagittal', '3d'].includes(event.data.view)
      ) {
        onActiveView(event.data.view as ProjectViewerSelectedView);
        return;
      }

      if (!onSliceThumbnail || !isProjectViewerSliceThumbnail(event.data)) {
        return;
      }

      if (
        event.data.studyInstanceUid !== studyInstanceUid ||
        event.data.seriesInstanceUid !== seriesInstanceUid
      ) {
        return;
      }

      onSliceThumbnail(event.data);
    };

    window.addEventListener('message', handleViewerMessage);
    return () => window.removeEventListener('message', handleViewerMessage);
  }, [
    onSliceThumbnail,
    onRotation,
    onActiveView,
    onRegistrationState,
    onRegistrationTransform,
    registrationAlignmentEnabled,
    registrationSyncRole,
    sendRequestedEditMode,
    sendRequestedParcelizations,
    sendRequestedPanReset,
    sendRequestedViewerState,
    sendRegistrationState,
    sendRegistrationMode,
    sendRegistrationTransforms,
    seriesInstanceUid,
    studyInstanceUid,
  ]);

  return (
    <iframe
      ref={iframeRef}
      key={`${viewerSrc}:${reloadVersion}`}
      className={className}
      data-testid="ohif-embedded-viewer"
      data-viewer-layout={hangingProtocolId}
      data-viewer-grid={gridLayout}
      loading={loading}
      onLoad={handleViewerLoad}
      onFocus={onViewerInteraction}
      src={viewerSrc}
      title="OHIF Embedded Viewer"
    />
  );
};

OHIFEmbeddedViewer.displayName = 'OHIFEmbeddedViewer';

// ProjectCreation owns many independent controls. Keep their state changes
// from reconciling the expensive iframe subtree when viewer inputs did not
// change.
export default React.memo(OHIFEmbeddedViewer);
